import { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import useGoBack from "hooks/useGoBack";
import SubHeader from "components/common/SubHeader";
import Blank from "components/common/Blank";
import useUserStore from "@store/useUserStore";
import ProfileAvatar from "components/common/ProfileAvatar";
import BottomSheet from "components/common/BottomSheet";
import {
    createInstructorProfile,
    getMyInstructorProfile,
    patchInstructorProfile,
    uploadInstructorProfileImage,
    deleteInstructorProfileImage,
    uploadInstructorProfileImages,
} from "api/instructor";
import { getLocationList, getSportsTypes } from "api/class";
import { useSnackbar } from "contexts/SnackbarContext";
import { INSTRUCTOR_PHOTO_UPLOAD_ENABLED } from "constants/featureFlags";

// 강사 상세 경력 필드 BE 제약 (BE issue #400 — 메인 1~5장, 자유 최대 10장 요청 기준)
const MAX_MAIN_SLIDES = 5;
const MAX_FREE_GALLERIES = 10;
const MAX_CAREER_SUMMARY_LEN = 500;
const MAX_CERT_GROUP_LABELS = 10;
const MAX_LABEL_LEN = 20;
const MAX_DETAILED_CAREERS = 10;
const MAX_CAREER_ROLE_LEN = 50;
const MAX_CAREER_YEARS = 50;
const MAX_BULLETS_PER_CAREER = 5;
const MAX_BULLET_LEN = 200;

// PATCH 전 상세 경력 정리: role 빈 카드 제외, years 0~50 보정, 빈 불릿 제거
const sanitizeDetailedCareers = (careers) =>
    (careers ?? [])
        .map((career) => ({
            role: (career.role ?? "").trim(),
            years: Math.min(
                MAX_CAREER_YEARS,
                Math.max(0, Number(career.years) || 0)
            ),
            bullets: (career.bullets ?? [])
                .map((bullet) => bullet.trim())
                .filter((bullet) => bullet !== ""),
        }))
        .filter((career) => career.role !== "");

const InstructorProfileEdit = () => {
    const goBack = useGoBack();
    const userInfo = useUserStore((state) => state.userInfo);
    const isInstructor = userInfo?.is_staff ?? false;
    const { showSuccess, showError } = useSnackbar();

    const [originalProfile, setOriginalProfile] = useState(null);
    const [instructorProfile, setInstructorProfile] = useState();
    const [instructorProfileImage, setInstructorProfileImage] = useState(null);
    const [sportsList, setSportsList] = useState([]);
    const [locationList, setLocationList] = useState([]);
    const [showInstructorImageSheet, setShowInstructorImageSheet] = useState(false);
    const [isUploadingInstructorImage, setIsUploadingInstructorImage] = useState(false);
    const [isUploadingSlides, setIsUploadingSlides] = useState(false);
    const [isUploadingGallery, setIsUploadingGallery] = useState(false);
    const [newGroupLabel, setNewGroupLabel] = useState("");
    const instructorFileInputRef = useRef(null);
    const slideFileInputRef = useRef(null);
    const galleryFileInputRef = useRef(null);

    useEffect(() => {
        if (!isInstructor) return;
        getSportsTypes().then((res) => {
            if (Array.isArray(res.sports)) setSportsList(res.sports);
        });
        getLocationList().then((res) => {
            const locations = Array.isArray(res) ? res : res?.locations;
            if (Array.isArray(locations)) setLocationList(locations);
        });
    }, [isInstructor]);

    useEffect(() => {
        if (!isInstructor) return;
        getMyInstructorProfile().then((res) => {
            if (res) {
                const profile = {
                    introduction: res.introduction,
                    interests: res.interests,
                    contact_phone: res.contact_phone,
                    contact_email: res.contact_email,
                    is_public: res.is_public ?? true,
                    sport_ids: res.sports?.map((s) => s.id) ?? [],
                    location_ids: res.locations?.map((location) => location.id) ?? [],
                    career_summary: res.career_summary ?? "",
                    certificate_group_labels: res.certificate_group_labels ?? [],
                    detailed_careers: (res.detailed_careers ?? []).map((career) => ({
                        role: career.role ?? "",
                        years: career.years ?? 0,
                        bullets: career.bullets ?? [],
                    })),
                    main_slides: res.main_slides ?? [],
                    free_galleries: res.free_galleries ?? [],
                };
                setOriginalProfile(profile);
                setInstructorProfile(profile);
                setInstructorProfileImage(res.profile_image_url ?? null);
            } else {
                // 신규 강사 (InstructorProfile row 미생성) — 빈 폼으로 강사 섹션 노출하여
                // 강습용 사진 업로드 onboarding 흐름이 작동할 수 있게 함
                setInstructorProfile({
                    introduction: "",
                    interests: "",
                    contact_phone: "",
                    contact_email: "",
                    is_public: true,
                    sport_ids: [],
                    location_ids: [],
                    career_summary: "",
                    certificate_group_labels: [],
                    detailed_careers: [],
                    main_slides: [],
                    free_galleries: [],
                });
            }
        });
    }, [isInstructor]);

    const handleInstructorImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploadingInstructorImage(true);
        try {
            let res;
            try {
                res = await uploadInstructorProfileImage(file);
            } catch (err) {
                const code = err?.response?.data?.code;
                if (code === "INSTRUCTOR_PROFILE_NOT_FOUND") {
                    await createInstructorProfile({
                        introduction: "",
                        interests: "",
                        contact_phone: "",
                        contact_email: "",
                        is_public: true,
                        sport_ids: [],
                        location_ids: [],
                    });
                    res = await uploadInstructorProfileImage(file);
                } else {
                    throw err;
                }
            }
            if (res?.profile_image_url) {
                setInstructorProfileImage(res.profile_image_url);
                showSuccess("강사 프로필 이미지가 변경되었습니다");
            }
        } catch (err) {
            const data = err?.response?.data;
            const serverMsg =
                data?.detail || data?.message || data?.error || data?.image?.[0];
            showError(serverMsg || "이미지 업로드에 실패했습니다");
        } finally {
            setIsUploadingInstructorImage(false);
            setShowInstructorImageSheet(false);
            e.target.value = "";
        }
    };

    const handleInstructorImageDelete = async () => {
        setIsUploadingInstructorImage(true);
        try {
            await deleteInstructorProfileImage();
            setInstructorProfileImage(null);
            showSuccess("강사 프로필 이미지가 삭제되었습니다");
        } catch (err) {
            const data = err?.response?.data;
            showError(data?.detail || data?.error || "이미지 삭제에 실패했습니다");
        } finally {
            setIsUploadingInstructorImage(false);
            setShowInstructorImageSheet(false);
        }
    };

    // 다중 이미지 업로드 공통: 업로드 후 발급 URL을 대상 필드에 append (limit 초과분 컷)
    const uploadImagesToField = async (e, field, purpose, limit, setUploading) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0) return;
        const remaining = limit - instructorProfile[field].length;
        if (remaining <= 0) {
            showError(`최대 ${limit}장까지 등록할 수 있습니다`);
            e.target.value = "";
            return;
        }
        if (files.length > remaining) {
            showError(`${remaining}장까지만 추가할 수 있어 앞의 ${remaining}장만 업로드합니다`);
        }
        setUploading(true);
        try {
            const res = await uploadInstructorProfileImages(
                files.slice(0, remaining),
                purpose
            );
            const urls = res?.image_urls ?? [];
            if (urls.length > 0) {
                setInstructorProfile((prev) => ({
                    ...prev,
                    [field]: [...prev[field], ...urls].slice(0, limit),
                }));
            }
        } catch (err) {
            const data = err?.response?.data;
            showError(data?.detail || data?.error || "이미지 업로드에 실패했습니다");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleSlideUpload = (e) =>
        uploadImagesToField(
            e,
            "main_slides",
            "main_slide",
            MAX_MAIN_SLIDES,
            setIsUploadingSlides
        );

    const handleGalleryUpload = (e) =>
        uploadImagesToField(
            e,
            "free_galleries",
            "free_gallery",
            MAX_FREE_GALLERIES,
            setIsUploadingGallery
        );

    const handleRemoveImageFromField = (field, targetIndex) => {
        setInstructorProfile((prev) => ({
            ...prev,
            [field]: prev[field].filter((url, index) => index !== targetIndex),
        }));
    };

    const handleAddGroupLabel = () => {
        const label = newGroupLabel.trim();
        if (!label) return;
        if (instructorProfile.certificate_group_labels.includes(label)) {
            setNewGroupLabel("");
            return;
        }
        if (instructorProfile.certificate_group_labels.length >= MAX_CERT_GROUP_LABELS) {
            showError(`자격 단체는 최대 ${MAX_CERT_GROUP_LABELS}개까지 등록할 수 있습니다`);
            return;
        }
        setInstructorProfile((prev) => ({
            ...prev,
            certificate_group_labels: [...prev.certificate_group_labels, label],
        }));
        setNewGroupLabel("");
    };

    const handleRemoveGroupLabel = (label) => {
        setInstructorProfile((prev) => ({
            ...prev,
            certificate_group_labels: prev.certificate_group_labels.filter(
                (item) => item !== label
            ),
        }));
    };

    const handleAddCareer = () => {
        if (instructorProfile.detailed_careers.length >= MAX_DETAILED_CAREERS) {
            showError(`상세 경력은 최대 ${MAX_DETAILED_CAREERS}개까지 등록할 수 있습니다`);
            return;
        }
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: [
                ...prev.detailed_careers,
                { role: "", years: 0, bullets: [""] },
            ],
        }));
    };

    const handleRemoveCareer = (careerIndex) => {
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: prev.detailed_careers.filter(
                (career, index) => index !== careerIndex
            ),
        }));
    };

    const handleCareerFieldChange = (careerIndex, field, value) => {
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: prev.detailed_careers.map((career, index) =>
                index === careerIndex ? { ...career, [field]: value } : career
            ),
        }));
    };

    const handleAddBullet = (careerIndex) => {
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: prev.detailed_careers.map((career, index) => {
                if (index !== careerIndex) return career;
                if (career.bullets.length >= MAX_BULLETS_PER_CAREER) return career;
                return { ...career, bullets: [...career.bullets, ""] };
            }),
        }));
    };

    const handleBulletChange = (careerIndex, bulletIndex, value) => {
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: prev.detailed_careers.map((career, index) =>
                index === careerIndex
                    ? {
                          ...career,
                          bullets: career.bullets.map((bullet, bIndex) =>
                              bIndex === bulletIndex ? value : bullet
                          ),
                      }
                    : career
            ),
        }));
    };

    const handleRemoveBullet = (careerIndex, bulletIndex) => {
        setInstructorProfile((prev) => ({
            ...prev,
            detailed_careers: prev.detailed_careers.map((career, index) =>
                index === careerIndex
                    ? {
                          ...career,
                          bullets: career.bullets.filter(
                              (bullet, bIndex) => bIndex !== bulletIndex
                          ),
                      }
                    : career
            ),
        }));
    };

    const saveInstructorProfile = async () => {
        // role 없는 빈 카드·빈 불릿은 저장 전 정리 (BE: role·years 필수)
        const payload = {
            ...instructorProfile,
            detailed_careers: sanitizeDetailedCareers(
                instructorProfile.detailed_careers
            ),
        };
        // 신규 생성 시 빈 main_slides는 전송 제외
        // (기존 프로필의 비우기는 diff-PATCH가 []를 전송 — BE 1~5장·비우기 허용 요청 기준)
        if (!originalProfile && payload.main_slides.length === 0) {
            delete payload.main_slides;
        }
        try {
            if (originalProfile) {
                const changed = Object.fromEntries(
                    Object.entries(payload).filter(([key, val]) => {
                        const orig = originalProfile[key];
                        if (Array.isArray(val)) {
                            return JSON.stringify(val) !== JSON.stringify(orig);
                        }
                        return val !== orig;
                    })
                );
                await patchInstructorProfile(changed);
                setOriginalProfile({ ...originalProfile, ...changed });
            } else {
                await createInstructorProfile(payload);
            }
            showSuccess("강사 프로필을 저장했습니다");
            // 회원정보 수정(UserEdit)과 동일하게 저장 성공 시 이전 화면으로 복귀
            goBack();
        } catch {
            showError("강사 프로필 저장에 실패했습니다");
        }
    };

    // 비강사 접근 가드
    if (!isInstructor) return <Navigate to="/mypage/user" replace />;

    if (!instructorProfile) {
        return (
            <div className="page-user-edit">
                <SubHeader title="강사 프로필 관리" />
                <Blank height={57} />
            </div>
        );
    }

    return (
        <div className="page-user-edit">
            <SubHeader title="강사 프로필 관리" />
            <Blank height={57} />
            <div className="ue-section">
                <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    ref={instructorFileInputRef}
                    style={{ display: "none" }}
                    onChange={handleInstructorImageUpload}
                />
                <div className="profile-image-edit-section">
                    <ProfileAvatar
                        imageUrl={instructorProfileImage}
                        size={100}
                        onClick={() => setShowInstructorImageSheet(true)}
                    />
                    <button
                        className="profile-image-change-btn"
                        onClick={() => setShowInstructorImageSheet(true)}
                        disabled={isUploadingInstructorImage}
                    >
                        {isUploadingInstructorImage ? "업로드 중..." : "강습용 사진 변경"}
                    </button>
                    <p
                        style={{
                            fontSize: 12,
                            color: "#757B80",
                            marginTop: 8,
                            textAlign: "center",
                        }}
                    >
                        강습 예약·연습반·투어 화면에 노출되는 강사 프로필 사진입니다.
                    </p>
                </div>
                <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    ref={slideFileInputRef}
                    style={{ display: "none" }}
                    onChange={handleSlideUpload}
                />
                <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    ref={galleryFileInputRef}
                    style={{ display: "none" }}
                    onChange={handleGalleryUpload}
                />
                <div className="input-wrap">
                    {/* BE 다중 이미지 업로드 API 배포 전까지 미노출 (BE issue #400) */}
                    {INSTRUCTOR_PHOTO_UPLOAD_ENABLED && (
                    <>
                    <div className="input-form">
                        <p className="label">
                            메인 사진 슬라이드
                            <span className="char-count">
                                {instructorProfile.main_slides.length}/{MAX_MAIN_SLIDES}
                            </span>
                        </p>
                        <div className="ue-photo-grid">
                            {instructorProfile.main_slides.map((url, index) => (
                                <div key={url} className="ue-photo-item">
                                    <img src={url} alt={`메인 사진 ${index + 1}`} />
                                    <button
                                        type="button"
                                        className="ue-photo-item__remove"
                                        aria-label={`메인 사진 ${index + 1} 삭제`}
                                        onClick={() =>
                                            handleRemoveImageFromField("main_slides", index)
                                        }
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {instructorProfile.main_slides.length < MAX_MAIN_SLIDES && (
                                <button
                                    type="button"
                                    className="ue-photo-add"
                                    disabled={isUploadingSlides}
                                    onClick={() => slideFileInputRef.current?.click()}
                                >
                                    {isUploadingSlides ? "업로드 중..." : "+"}
                                </button>
                            )}
                        </div>
                        <p className="instructor-location-help">
                            강사 프로필 상단에 슬라이드로 노출됩니다. 최대 {MAX_MAIN_SLIDES}장,
                            비우면 강습용 사진이 대신 노출됩니다.
                        </p>
                    </div>
                    <div className="input-form">
                        <p className="label">
                            활동 사진
                            <span className="char-count">
                                {instructorProfile.free_galleries.length}/{MAX_FREE_GALLERIES}
                            </span>
                        </p>
                        <div className="ue-photo-grid">
                            {instructorProfile.free_galleries.map((url, index) => (
                                <div key={url} className="ue-photo-item">
                                    <img src={url} alt={`활동 사진 ${index + 1}`} />
                                    <button
                                        type="button"
                                        className="ue-photo-item__remove"
                                        aria-label={`활동 사진 ${index + 1} 삭제`}
                                        onClick={() =>
                                            handleRemoveImageFromField("free_galleries", index)
                                        }
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {instructorProfile.free_galleries.length < MAX_FREE_GALLERIES && (
                                <button
                                    type="button"
                                    className="ue-photo-add"
                                    disabled={isUploadingGallery}
                                    onClick={() => galleryFileInputRef.current?.click()}
                                >
                                    {isUploadingGallery ? "업로드 중..." : "+"}
                                </button>
                            )}
                        </div>
                        <p className="instructor-location-help">
                            강사 프로필의 활동 사진 갤러리에 노출됩니다.
                        </p>
                    </div>
                    </>
                    )}
                    <div className="input-form">
                        <p className="label">종목</p>
                        <div className="association-btn-group">
                            {sportsList.map((sport) => (
                                <button
                                    key={sport.id}
                                    type="button"
                                    className={`association-btn${
                                        instructorProfile.sport_ids.includes(sport.id)
                                            ? " active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setInstructorProfile((prev) => ({
                                            ...prev,
                                            sport_ids: prev.sport_ids.includes(sport.id)
                                                ? prev.sport_ids.filter((id) => id !== sport.id)
                                                : [...prev.sport_ids, sport.id],
                                        }))
                                    }
                                >
                                    {sport.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="input-form">
                        <p className="label">주요 활동 장소</p>
                        <div className="association-btn-group instructor-location-btn-group">
                            {locationList.map((location) => (
                                <button
                                    key={location.id}
                                    type="button"
                                    className={`association-btn${
                                        instructorProfile.location_ids.includes(location.id)
                                            ? " active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setInstructorProfile((prev) => ({
                                            ...prev,
                                            location_ids: prev.location_ids.includes(location.id)
                                                ? prev.location_ids.filter((id) => id !== location.id)
                                                : [...prev.location_ids, location.id],
                                        }))
                                    }
                                >
                                    {location.name}
                                </button>
                            ))}
                        </div>
                        <p className="instructor-location-help">
                            선택한 장소는 수업 요청 후보 매칭에 사용됩니다.
                        </p>
                    </div>
                    <div className="input-form">
                        <p className="label">연락처</p>
                        <input
                            className="input"
                            placeholder="010-0000-0000"
                            value={instructorProfile.contact_phone}
                            onChange={(e) =>
                                setInstructorProfile((prev) => ({
                                    ...prev,
                                    contact_phone: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">이메일</p>
                        <input
                            className="input"
                            type="email"
                            placeholder="ex@buccl.com"
                            value={instructorProfile.contact_email}
                            onChange={(e) =>
                                setInstructorProfile((prev) => ({
                                    ...prev,
                                    contact_email: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">관심사</p>
                        <input
                            className="input"
                            placeholder="관심사를 입력해주세요"
                            value={instructorProfile.interests}
                            onChange={(e) =>
                                setInstructorProfile((prev) => ({
                                    ...prev,
                                    interests: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">강사 소개글</p>
                        <textarea
                            className="ue-textarea"
                            placeholder="강사 소개를 입력해주세요"
                            value={instructorProfile.introduction}
                            rows={6}
                            onChange={(e) =>
                                setInstructorProfile((prev) => ({
                                    ...prev,
                                    introduction: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">
                            주요 경력 요약
                            <span className="char-count">
                                {(instructorProfile.career_summary ?? "").length}/{MAX_CAREER_SUMMARY_LEN}
                            </span>
                        </p>
                        <textarea
                            className="ue-textarea"
                            placeholder="주요 경력을 한두 문장으로 요약해주세요 (예: 보홀, 미야코지마 등 다수 투어 진행)"
                            value={instructorProfile.career_summary ?? ""}
                            rows={3}
                            maxLength={MAX_CAREER_SUMMARY_LEN}
                            onChange={(e) =>
                                setInstructorProfile((prev) => ({
                                    ...prev,
                                    career_summary: e.target.value,
                                }))
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">자격 단체</p>
                        <div className="ue-chip-input-row">
                            <input
                                className="input"
                                placeholder="예: PADI, SSI"
                                value={newGroupLabel}
                                maxLength={MAX_LABEL_LEN}
                                onChange={(e) => setNewGroupLabel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
                                    e.preventDefault();
                                    handleAddGroupLabel();
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-purple500 ue-inline-btn"
                                onClick={handleAddGroupLabel}
                            >
                                추가
                            </button>
                        </div>
                        {instructorProfile.certificate_group_labels.length > 0 && (
                            <div className="ue-chip-list">
                                {instructorProfile.certificate_group_labels.map((label) => (
                                    <span key={label} className="ue-chip">
                                        {label}
                                        <button
                                            type="button"
                                            className="ue-chip__remove"
                                            aria-label={`${label} 삭제`}
                                            onClick={() => handleRemoveGroupLabel(label)}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <p className="instructor-location-help">
                            강사 프로필 상단 요약에 칩 형태로 노출됩니다. (최대 {MAX_CERT_GROUP_LABELS}개)
                        </p>
                    </div>
                    <div className="input-form">
                        <p className="label">상세 경력</p>
                        <div className="ue-career-list">
                            {instructorProfile.detailed_careers.map((career, careerIndex) => (
                                <div key={careerIndex} className="ue-career-card">
                                    <div className="ue-career-card__head">
                                        <input
                                            className="input ue-career-card__role"
                                            placeholder="역할 (예: 프리다이빙 강사)"
                                            value={career.role}
                                            maxLength={MAX_CAREER_ROLE_LEN}
                                            onChange={(e) =>
                                                handleCareerFieldChange(
                                                    careerIndex,
                                                    "role",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="ue-career-card__years">
                                            <input
                                                className="input"
                                                type="number"
                                                inputMode="numeric"
                                                min={0}
                                                max={MAX_CAREER_YEARS}
                                                aria-label="경력 연차"
                                                value={career.years}
                                                onChange={(e) =>
                                                    handleCareerFieldChange(
                                                        careerIndex,
                                                        "years",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <span>년</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="ue-career-card__remove"
                                            aria-label="경력 삭제"
                                            onClick={() => handleRemoveCareer(careerIndex)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div className="ue-career-card__bullets">
                                        {career.bullets.map((bullet, bulletIndex) => (
                                            <div
                                                key={bulletIndex}
                                                className="ue-career-card__bullet-row"
                                            >
                                                <input
                                                    className="input"
                                                    placeholder="활동 내용 (예: 수도권 주요 다이빙풀 강습 다수 진행)"
                                                    value={bullet}
                                                    maxLength={MAX_BULLET_LEN}
                                                    onChange={(e) =>
                                                        handleBulletChange(
                                                            careerIndex,
                                                            bulletIndex,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    className="ue-career-card__bullet-remove"
                                                    aria-label="활동 내용 삭제"
                                                    onClick={() =>
                                                        handleRemoveBullet(
                                                            careerIndex,
                                                            bulletIndex
                                                        )
                                                    }
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                        {career.bullets.length < MAX_BULLETS_PER_CAREER && (
                                            <button
                                                type="button"
                                                className="ue-career-card__add-bullet"
                                                onClick={() => handleAddBullet(careerIndex)}
                                            >
                                                + 활동 내용 추가
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {instructorProfile.detailed_careers.length < MAX_DETAILED_CAREERS && (
                                <button
                                    type="button"
                                    className="ue-career-add"
                                    onClick={handleAddCareer}
                                >
                                    + 경력 카드 추가
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="radio-input-wrap">
                        <p className="label">공개 여부</p>
                        <div className="radio-group">
                            <input
                                type="radio"
                                id="is_public_true"
                                name="is_public"
                                className="custom-radio-input"
                                checked={instructorProfile.is_public}
                                onChange={() =>
                                    setInstructorProfile((prev) => ({
                                        ...prev,
                                        is_public: true,
                                    }))
                                }
                            />
                            <label
                                htmlFor="is_public_true"
                                className="custom-radio-label"
                            >
                                공개
                            </label>
                            <input
                                type="radio"
                                id="is_public_false"
                                name="is_public"
                                className="custom-radio-input"
                                checked={!instructorProfile.is_public}
                                onChange={() =>
                                    setInstructorProfile((prev) => ({
                                        ...prev,
                                        is_public: false,
                                    }))
                                }
                            />
                            <label
                                htmlFor="is_public_false"
                                className="custom-radio-label"
                            >
                                비공개
                            </label>
                        </div>
                    </div>
                </div>
                <button
                    className="btn btn-purple500 btn-large"
                    onClick={saveInstructorProfile}
                >
                    강사 프로필 저장
                </button>
            </div>
            <BottomSheet
                isOpen={showInstructorImageSheet}
                onClose={() => setShowInstructorImageSheet(false)}
                height="auto"
            >
                <div className="image-action-list">
                    <button onClick={() => instructorFileInputRef.current?.click()}>
                        앨범에서 선택
                    </button>
                    {instructorProfileImage && (
                        <button
                            className="delete"
                            onClick={handleInstructorImageDelete}
                        >
                            현재 사진 삭제
                        </button>
                    )}
                </div>
            </BottomSheet>
        </div>
    );
};

export default InstructorProfileEdit;
